import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useProductNavigation } from '../../../../shared/hooks/useProductNavigation';
import trendingProductService from '../../services/trendingproducts.service';
import { TrendingProduct } from '../../dto/trendingproducts.dto';
import { useCart } from '../../../../contexts/CartContext';
import { useWishlist } from '../../../wishlist/WishlistContext';
import VariantOptionModalMobile from '../../../../shared/components/VariantOptionModal/VariantOptionModalMobile';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/TrendingProducts.Mobile.css';
import ProductCardMobile from '@/shared/components/ProductCardsA/ProductCardMobile';
import LoadingSpinner from '../../../../shared/components/LodingSpiner/LodingSpiner';

const convertVariantsForDisplay = (product: TrendingProduct) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  return product.variants.map((variant: any) => {
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    return baseVariant;
  });
};

const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
  if (!variants || variants.length === 0) return undefined;
  
  const firstVariant = variants[0];
  if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
    return undefined;
  }
  
  if (firstVariant.variantLevels) {
    return Object.keys(firstVariant.variantLevels).map((key: string) => {
      const match = key.match(/level(\d+)/i);
      if (match) {
        return firstVariant.variantLevels[key];
      }
      return firstVariant.variantLevels[key];
    });
  }
  
  const attributeKeys = Object.keys(firstVariant.attributes);
  if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
    return attributeKeys;
  }
  
  return undefined;
};

const TrendingProducts: React.FC = () => {
  const [products, setProducts] = useState<TrendingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalProduct, setModalProduct] = useState<TrendingProduct | null>(null);
  const navigateToProduct = useProductNavigation();
  const dragDistanceRef = useRef<number>(0);

  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: (modalProduct as any).variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  useEffect(() => {
    fetchTrendingProducts();
  }, []);

  const fetchTrendingProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trendingProductService.getTrendingProducts();
      if (response.success && response.data) {
        setProducts(response.data);
      } else {
        setError('Failed to load trending products');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching trending products');
    } finally {
      setLoading(false);
    }
  };

  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);

  const getTruncatedTitle = (title?: string, maxWords: number = 6) => {
    const safeTitle = title?.trim() || 'Item';
    const words = safeTitle.split(/\s+/);
    if (words.length <= maxWords) return safeTitle;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const handleAddToCart = (product: TrendingProduct) => {
    try {
      const cartProduct = {
        id: product._id,
        _id: product._id,
        title: product.title || 'Unnamed Product',
        name: product.title || 'Unnamed Product',
        price: product.price || 0,
        imageUrl: product.imageUrl || '',
        description: product.description || '',
        quantity: 1
      };
      
      addToCart(cartProduct, 1);
    } catch (error) {
    }
  };

  const handleMoreInfo = (product: TrendingProduct) => {
    // Only navigate if user hasn't dragged (moved less than 10px)
    if (product.slug && dragDistanceRef.current < 10) {
      navigateToProduct(product.slug);
    }
  };

  const handleWishlistToggle = (product: TrendingProduct) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return;
    lastWishlistActionRef.current = now;

    try {
      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
        toast.info(`${getTruncatedTitle(product.title)} removed from wishlist`, {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: product.imageUrl ? [product.imageUrl] : [],
          isActive: product.isActive,
          isFeatured: product.isFeatured ?? false,
          hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
          categories: (product.categories || []).map(c => c.name),
          tags: product.tags || []
        } as any;

        addToWishlist(productForWishlist);
        toast.success(`${getTruncatedTitle(product.title)} added to wishlist`, {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };



  if (loading) {
    return (
      <div className="trending-products-section-mobile" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="trending-products-section-mobile">
        <div className="error-message-mobile">
          {error}
          <button onClick={fetchTrendingProducts} className="retry-button-mobile">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="trending-products-section-mobile">
        <div className="trending-products-container-mobile">
          <h2 className="trending-products-title-mobile">Trending Products</h2>
          <div
            className="tp-products-row-mobile"
          style={{
            display: 'flex',
            gap: '1rem',
            padding: '1rem 0',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            maxWidth: '100%',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {products.map((product) => {
            let imgSrc = product.imageUrl;
            if (!imgSrc) {
              imgSrc = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
            }
            return (
              <ProductCardMobile
                key={product._id}
                id={product._id}
                title={product.title}
                price={`${Math.round(product.price || 0)}`}
                image={imgSrc}
                slug={product.slug}
                deliveryTag={true}
                onAddToCart={() => handleAddToCart(product)}
                onAddToWishlist={() => handleWishlistToggle(product)}
                onViewOptions={() => setModalProduct(product)}
                isWishlisted={isItemInWishlist(product._id)}
                onMoreInfo={() => handleMoreInfo(product)}
                quantity={product.quantity}
                variants={product.variants}
                hierarchicalVariants={product.variants}
                variantHierarchy={(product as any).variantHierarchy}
              />
            );
          })}
          </div>
        </div>
      </div>
      {/* Variant Options Modal - Rendered outside section to avoid stacking context issues */}
      {modalProduct && modalData && (
        <VariantOptionModalMobile
          isOpen={!!modalProduct}
          onClose={() => setModalProduct(null)}
          product={modalProduct}
          variants={modalData.variants}
          hierarchicalVariants={modalData.hierarchicalVariants}
          variantHierarchy={modalData.variantHierarchy}
        />
      )}
    </>
  );
};

export default TrendingProducts;